import React, { useEffect } from "react";
import "./BookingTracking.scss";
import { PassengerName } from "./PassengerName";
import { BookingAddresses } from "./BookingAddresses";
import { TrackingMap } from "./TrackingMap";
import { BookingFareSection } from "./BookingFareSection";
import { PaymentConfirmationButton } from "./PaymentConfirmationButton";
import { TermsAndConditions } from "./TermsAndConditions";
import { TrackingActionButtonGroup } from "./TrackingActionButtonGroup";
import { MaybeConfirmedFixedPriceSection } from "./MaybeConfirmedFixedPriceSection";
import { BookingStatusDisplay } from "./BookingStatusDisplay";
import { BookingReference } from "./BookingReference";
import { MaybePaymentSection } from "./MaybePaymentSection";
import { DriverDetailsSection } from "./DriverDetailsSection";
import { Notice } from "./Notifications/Notice";
import { Toaster } from "./Notifications/Toaster";
import { useAppState } from "../../../Redux/ReduxHooks";
import { BookingTrackingSkeleton } from "./BookingTrackingSkeleton";
import { BookingRefreshTimer } from "../BookingRefreshTimer";
import { GoogleMapScriptLoader } from "../../../widgets/google-maps/GoogleMapScriptLoader";
import { LoginStatusKind, LogoutKind } from "../../Authentication/AuthEntities";
import Auth from "../../Authentication/AuthImpl";

/**
 * Main container that contains all the booking tracking and payment related components.
 */
export const BookingTrackingContainer: React.FC = () => {

    const bookingDetails = useAppState(state => state.PhoneToCnp.Booking);
    const isLoading = useAppState(state => state.PhoneToCnp.IsLoadingBookingDetails);
    const isLoggedIn = useAppState(state => state.authentication.LoginStatus === LoginStatusKind.LoggedIn);

    // Don't show the UI if booking details is not populated yet.
    useEffect(() => {
        // Logout the user from the website session to avoid any confusion with the IVR booking flow. If the user is logged in to the website in another tab, it can cause issues with the booking flow and payment flow. Hence, logout the user from the website session on this page.
        if (isLoggedIn) {
            new Auth().logout(LogoutKind.Website);
        }

        if (!bookingDetails) return;
    }, [bookingDetails, isLoggedIn]);

    // Show skeleton loader while booking details are being fetched
    if (isLoading) {
        return <BookingTrackingSkeleton />;
    }

    // Don't show anything if booking details haven't loaded yet and not loading
    if (!bookingDetails) return null;

    return (
        <div className="tracking-outer-container">
            <Notice />            
            <div className="tracking-main-container">
                {/* Adds a google maps API script <GoogleMapScriptLoader>, as required for ETA loading */}
                <GoogleMapScriptLoader><BookingRefreshTimer /></GoogleMapScriptLoader>
                <TrackingMap />
                <BookingStatusDisplay />
                <DriverDetailsSection />
                <PassengerName />
                <BookingAddresses />
                <BookingFareSection />
                <MaybePaymentSection />
                <PaymentConfirmationButton />
                <TermsAndConditions />
                <MaybeConfirmedFixedPriceSection />
                <BookingReference />
                <TrackingActionButtonGroup />
                <Toaster />
            </div>
        </div>
    )
}